import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils/cn";
import { colorizeJson } from "@/lib/utils/colorize-json";
import { cva } from "class-variance-authority";
import * as React from "react";
import ReactTextareaAutosize, { type TextareaAutosizeProps } from "react-textarea-autosize";

const textareaVariants = cva(
  "flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm placeholder:text-placeholder ring-ring/10 outline-ring/50 dark:ring-ring/20 dark:outline-ring/40 focus-visible:ring-4 focus-visible:outline-1 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
);

const TextareaBase: React.FC<React.ComponentProps<"textarea">> = ({ className, ...props }) => (
  <textarea
    data-slot="textarea"
    className={cn(textareaVariants({ className }))}
    {...props}
  />
);
TextareaBase.displayName = "Textarea";

const Autosize = React.forwardRef<HTMLTextAreaElement, TextareaAutosizeProps>(
  ({ className, minRows = 1, ...props }, ref) => (
    <ReactTextareaAutosize
      ref={ref}
      minRows={minRows}
      // An autosize textarea grows to fit its content, so it should never show a
      // scrollbar. Leaving `overflow: auto` causes a feedback loop: once content
      // overflows, the reserved scrollbar narrows the box, content reflows taller,
      // and the height can never settle (phantom scroll). `overflow-hidden` fixes it.
      className={cn(Input.variants(), "h-auto py-[7px] resize-none overflow-hidden", className)}
      {...props}
    />
  ),
);
Autosize.displayName = "Textarea.Autosize";

type JsonProps = Omit<TextareaAutosizeProps, "value" | "onChange"> & {
  value: string;
  onChange: (value: string) => void;
};

const Json = React.forwardRef<HTMLTextAreaElement, JsonProps>(
  ({ value, onChange, className, ...props }, ref) => {
    const colorized = React.useMemo(() => colorizeJson(value), [value]);
    return (
      <div className="relative">
        <pre
          className="pointer-events-none absolute inset-px overflow-hidden whitespace-pre-wrap break-words px-3 py-2 font-mono text-base md:text-sm"
          aria-hidden
        >
          {colorized}
          {"\n "}
        </pre>
        <Autosize
          ref={ref}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={cn(
            "relative bg-transparent text-transparent caret-foreground font-mono",
            className,
          )}
          {...props}
        />
      </div>
    );
  },
);
Json.displayName = "Textarea.Json";

export const Textarea = Object.assign(TextareaBase, {
  variants: textareaVariants,
  Autosize,
  Json,
});
